import type { OrderStatusValue } from '@my/tms-api';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type OrdersStatusFilter = OrderStatusValue | 'all';
export type OrdersSortKey = 'orderNumber' | 'customer' | 'startPoint' | 'nextPoint';
export type OrdersSortDir = 'asc' | 'desc';

interface OrdersViewState {
  search: string;
  status: OrdersStatusFilter;
  /** `null` is the default order, newest first. */
  sortKey: OrdersSortKey | null;
  sortDir: OrdersSortDir;
  /**
   * The Completed / Cancelled date range, `yyyy-MM-dd`. `null` means "this
   * calendar month" worked out when the screen opens, so an untouched range
   * moves on with the calendar instead of freezing at the month it was first
   * saved; `''` is a bound the user cleared.
   */
  from: string | null;
  to: string | null;
  setSearch: (search: string) => void;
  setStatus: (status: OrdersStatusFilter) => void;
  setSortKey: (sortKey: OrdersSortKey | null) => void;
  setSortDir: (sortDir: OrdersSortDir) => void;
  setFrom: (from: string | null) => void;
  setTo: (to: string | null) => void;
}

/**
 * The Orders screen's filters and sort, kept so opening an order and coming
 * back — or a refresh — lands on the same list rather than resetting to All.
 * Per browser, in localStorage (same idea as the Work View store).
 */
export const useOrdersViewStore = create<OrdersViewState>()(
  persist(
    (set) => ({
      search: '',
      status: 'all',
      sortKey: null,
      sortDir: 'asc',
      from: null,
      to: null,
      setSearch: (search) => set({ search }),
      setStatus: (status) => set({ status }),
      setSortKey: (sortKey) => set({ sortKey }),
      setSortDir: (sortDir) => set({ sortDir }),
      setFrom: (from) => set({ from }),
      setTo: (to) => set({ to }),
    }),
    {
      name: 'my:tms:ordersView',
      partialize: (s) => ({
        search: s.search,
        status: s.status,
        sortKey: s.sortKey,
        sortDir: s.sortDir,
        from: s.from,
        to: s.to,
      }),
    },
  ),
);
